<template>
  <AuthLayout
    data-testid="set-new-password"
    :loading="loading"
    :disabled="loading"
  >
    <AuthLoginLogo />

    <form
      v-if="stage === 1"
      class="set-new-password__form"
      data-testid="set-new-password__form"
      @submit.prevent="setPassword"
    >
                  <header class="set-new-password__header">
                    <h1
                      class="set-new-password__title"
                      data-testid="set-new-password__title"
                    >
                      {{ copy.SET_PASSWORD_HEADING }}
                    </h1>
                    <p
                      class="set-new-password__subtitle"
                      data-testid="set-new-password__subtitle"
                    >
                      {{ copy.SET_PASSWORD_SUBTITLE }}
                    </p>
                  </header>

                  <AuthLoginAlert
                    :message="error"
                    test-id="set-new-password__alert"
                  />

                  <div class="set-new-password__fields">
                    <div class="set-new-password__field">
                      <label class="set-new-password__label" for="set-new-password-email">
                        {{ copy.SET_PASSWORD_EMAIL_LABEL }}
                      </label>
                      <div
                        class="set-new-password__control"
                        :class="{ 'set-new-password__control--filled': Boolean(form.email) }"
                      >
                        <input
                          id="set-new-password-email"
                          v-model="form.email"
                          class="set-new-password__input"
                          type="email"
                          name="email"
                          autocomplete="username"
                          :disabled="loading"
                          data-testid="set-new-password__email"
                        />
                      </div>
                    </div>

                    <div v-if="!form.otp" class="set-new-password__field">
                      <label class="set-new-password__label" for="set-new-password-old">
                        {{ copy.SET_PASSWORD_OLD_LABEL }}
                      </label>
                      <div
                        class="set-new-password__control"
                        :class="{ 'set-new-password__control--filled': Boolean(form.oldpassword) }"
                      >
                        <input
                          id="set-new-password-old"
                          v-model="form.oldpassword"
                          class="set-new-password__input text-transform-none"
                          :type="showOldPassword ? 'text' : 'password'"
                          name="current-password"
                          autocomplete="current-password"
                          :disabled="loading"
                          data-testid="set-new-password__old-password"
                        />
                        <button
                          type="button"
                          class="set-new-password__toggle"
                          :disabled="loading"
                          :aria-label="showOldPassword ? 'Hide password' : 'Show password'"
                          data-testid="set-new-password__old-password-toggle"
                          @click="showOldPassword = !showOldPassword"
                        >
                          <img
                            class="set-new-password__toggle-icon"
                            :src="eyeIcon(showOldPassword)"
                            width="20"
                            height="20"
                            alt=""
                          />
                        </button>
                      </div>
                    </div>

                    <div v-else class="set-new-password__field">
                      <label class="set-new-password__label" for="set-new-password-otp">
                        {{ copy.SET_PASSWORD_OTP_LABEL }}
                      </label>
                      <div
                        class="set-new-password__control"
                        :class="{ 'set-new-password__control--filled': Boolean(form.otp) }"
                      >
                        <input
                          id="set-new-password-otp"
                          v-model="form.otp"
                          class="set-new-password__input"
                          type="text"
                          inputmode="numeric"
                          name="one-time-code"
                          autocomplete="one-time-code"
                          :disabled="loading"
                          data-testid="set-new-password__otp"
                        />
                      </div>
                    </div>

                    <div class="set-new-password__field">
                      <label class="set-new-password__label" for="set-new-password-new">
                        {{ copy.SET_PASSWORD_NEW_LABEL }}
                      </label>
                      <div
                        class="set-new-password__control"
                        :class="{ 'set-new-password__control--filled': Boolean(form.password) }"
                      >
                        <input
                          id="set-new-password-new"
                          v-model="form.password"
                          class="set-new-password__input text-transform-none"
                          :type="showPassword ? 'text' : 'password'"
                          name="new-password"
                          autocomplete="new-password"
                          :disabled="loading"
                          data-testid="set-new-password__new-password"
                          @input="checkPolicy"
                        />
                        <button
                          type="button"
                          class="set-new-password__toggle"
                          :disabled="loading"
                          :aria-label="showPassword ? 'Hide password' : 'Show password'"
                          data-testid="set-new-password__new-password-toggle"
                          @click="showPassword = !showPassword"
                        >
                          <img
                            class="set-new-password__toggle-icon"
                            :src="eyeIcon(showPassword)"
                            width="20"
                            height="20"
                            alt=""
                          />
                        </button>
                      </div>
                    </div>

                    <div class="set-new-password__field">
                      <label class="set-new-password__label" for="set-new-password-confirm">
                        {{ copy.SET_PASSWORD_CONFIRM_LABEL }}
                      </label>
                      <div
                        class="set-new-password__control"
                        :class="{ 'set-new-password__control--filled': Boolean(form.repassword) }"
                      >
                        <input
                          id="set-new-password-confirm"
                          v-model="form.repassword"
                          class="set-new-password__input text-transform-none"
                          :type="showConfirmPassword ? 'text' : 'password'"
                          name="confirm-password"
                          autocomplete="new-password"
                          :disabled="loading"
                          data-testid="set-new-password__confirm-password"
                        />
                        <button
                          type="button"
                          class="set-new-password__toggle"
                          :disabled="loading"
                          :aria-label="showConfirmPassword ? 'Hide password' : 'Show password'"
                          data-testid="set-new-password__confirm-password-toggle"
                          @click="showConfirmPassword = !showConfirmPassword"
                        >
                          <img
                            class="set-new-password__toggle-icon"
                            :src="eyeIcon(showConfirmPassword)"
                            width="20"
                            height="20"
                            alt=""
                          />
                        </button>
                      </div>
                      <p
                        v-if="showMismatch"
                        class="set-new-password__mismatch"
                        data-testid="set-new-password__mismatch"
                      >
                        {{ copy.SET_PASSWORD_MISMATCH }}
                      </p>
                    </div>
                  </div>

                  <div class="set-new-password__hint" data-testid="set-new-password__policy">
                    <v-icon
                      class="set-new-password__hint-icon"
                      :color="policyMatch ? 'success' : 'error'"
                      small
                    >
                      {{ policyMatch ? 'mdi-check-circle' : 'mdi-alert-circle' }}
                    </v-icon>
                    <p class="set-new-password__hint-text">{{ copy.SET_PASSWORD_POLICY }}</p>
                  </div>

                  <div class="set-new-password__actions">
                    <v-btn
                      class="set-new-password__primary"
                      color="primary"
                      block
                      large
                      elevation="0"
                      type="submit"
                      :disabled="!canSubmit"
                      data-testid="set-new-password__submit"
                    >
                      <span>{{ copy.SET_PASSWORD_SUBMIT }}</span>
                    </v-btn>
                    <v-btn
                      class="set-new-password__cancel"
                      block
                      outlined
                      elevation="0"
                      type="button"
                      to="/"
                      data-testid="set-new-password__cancel"
                    >
                      <span>{{ copy.SET_PASSWORD_CANCEL }}</span>
                    </v-btn>
                  </div>
                </form>

                <div
                  v-else-if="stage === 2"
                  class="set-new-password__success"
                  data-testid="set-new-password__success"
                >
                  <h1
                    class="set-new-password__title"
                    data-testid="set-new-password__success-title"
                  >
                    {{ copy.SET_PASSWORD_SUCCESS_HEADING }}
                  </h1>
                  <AuthLoginAlert
                    :message="error"
                    test-id="set-new-password__success-alert"
                  />
                  <p
                    v-if="goToLogin"
                    class="set-new-password__redirect"
                    data-testid="set-new-password__redirect"
                  >
                    {{ copy.SET_PASSWORD_REDIRECTING }}
                  </p>
                  <router-link
                    v-else
                    to="/"
                    class="set-new-password__back-link"
                    data-testid="set-new-password__home"
                  >
                    {{ copy.SET_PASSWORD_GO_HOME }}
                  </router-link>
                </div>

                <div v-else class="set-new-password__fallback" data-testid="set-new-password__noaccess">
                  <noaccess class="pb-3" />
                </div>
  </AuthLayout>
</template>

<script>
import noaccess from '@/views/common/NoAccess';
import constants from '../../plugins/constants';
import { passwordMeetsPolicy } from './utils/authLoginUtils';
import AuthLayout from './components/AuthLayout.vue';
import AuthLoginAlert from './components/AuthLoginAlert.vue';
import AuthLoginLogo from './components/AuthLoginLogo.vue';

const { AUTH } = constants;
const EMPTY_FORM = () => ({
  oldpassword: '',
  password: '',
  repassword: '',
  email: '',
  otp: '',
});

export default {
  name: 'SetNewPassword',
  components: {
    noaccess,
    AuthLayout,
    AuthLoginAlert,
    AuthLoginLogo,
  },
  props: {
    props: { type: Object, default: () => ({}) },
  },
  data: () => ({
    stage: 1,
    loading: false,
    form: EMPTY_FORM(),
    error: '',
    goToLogin: false,
    policyMatch: false,
    showOldPassword: false,
    showPassword: false,
    showConfirmPassword: false,
    redirectTimer: null,
    copy: AUTH,
  }),
  computed: {
    passwordsMatch() {
      return Boolean(this.form.password) && this.form.password === this.form.repassword;
    },
    showMismatch() {
      return Boolean(this.form.password && this.form.repassword && !this.passwordsMatch);
    },
    canSubmit() {
      return Boolean(
        this.form.email &&
          (this.form.otp || this.form.oldpassword) &&
          this.policyMatch &&
          this.passwordsMatch &&
          !this.loading,
      );
    },
  },
  created() {
    this.form.email = this.$route.query.u || '';
    this.form.otp = this.$route.query.otp || '';
    if (this.form.email) {
      this.checkSetPassword();
    } else if (this.form.otp) {
      this.$store.commit('addLoading');
      this.stage = 1;
      this.$nextTick(() => {
        this.$store.commit('removeLoading');
      });
    }
  },
  beforeDestroy() {
    if (this.redirectTimer) {
      clearTimeout(this.redirectTimer);
      this.redirectTimer = null;
    }
    this.stage = 1;
    this.loading = false;
    this.form = EMPTY_FORM();
    this.error = '';
    this.goToLogin = false;
    this.policyMatch = false;
    this.showOldPassword = false;
    this.showPassword = false;
    this.showConfirmPassword = false;
  },
  methods: {
    eyeIcon(visible) {
      return visible ? '/img/icons/EyeIcon.svg' : '/img/auth/icon-eye-off.svg';
    },
    checkPolicy() {
      this.policyMatch = passwordMeetsPolicy(this.form.password);
    },
    async checkSetPassword() {
      this.error = '';
      this.loading = true;
      this.$store.commit('addLoading');
      console.log('[SetNewPassword]:', { action: 'checkSetPassword' });
      try {
        const result = await this.$store.dispatch('auth/checkSetPassword', this.form.email);
        if (result?.success) {
          this.stage = 1;
        } else if (result?.error) {
          this.stage = 3;
          this.error = result.message || this.copy.RESET_GENERIC_ERROR;
        } else {
          this.error = result?.message || this.copy.RESET_GENERIC_ERROR;
        }
      } catch (error) {
        this.stage = 3;
        this.error = error?.message || this.copy.RESET_GENERIC_ERROR;
      } finally {
        this.loading = false;
        this.$store.commit('removeLoading');
        this.checkPolicy();
      }
    },
    async setPassword() {
      if (!this.canSubmit) return;
      this.error = '';
      this.loading = true;
      console.log('[SetNewPassword]:', { action: 'setPassword' });
      try {
        const result = await this.$store.dispatch('auth/setPassword', this.form);
        if (result?.success) {
          this.stage = 2;
          this.goToLogin = true;
          this.redirectTimer = setTimeout(() => {
            window.location.href = '/';
          }, this.copy.SET_PASSWORD_REDIRECT_MS);
        } else {
          this.error = result?.message || this.copy.RESET_GENERIC_ERROR;
        }
      } catch (error) {
        this.error = error?.message || this.copy.RESET_GENERIC_ERROR;
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import './SetNewPassword.scss';
</style>
